import { httpRequest } from '@/utils/axios';
import { getStableClientCyyInfo } from '@/utils/currency/client-currency';
import { IProductListApiParams, ProductDetailGiftPayload } from './types/product';

export const fetchRecommendProducts = (params: {
  group_id?: number;
  strategy?: number;
  waterfall_position?: number;
}) =>
  httpRequest('GET:/api/public/recommend_products', {
    params: {
      ...params,
      ...getStableClientCyyInfo(),
    },
  });

export const fetchGoodsDetail = (sku: string, get_promotions?: number) =>
  httpRequest('GET:/api/products/show/{sku}', {
    sku,
    params: get_promotions
      ? { get_promotions, ...getStableClientCyyInfo() }
      : { ...getStableClientCyyInfo() },
  });

export const fetchLensShow = (params: {
  prescription_type: string;
  product_id: string;
  od_sph: string;
  os_sph: string;
  od_cyl: string;
  os_cyl: string;
  os_add: string;
  od_add: string;
  is_prism: string;
  prescription_id?: number;
}) => httpRequest('GET:/api/lens/lens_show', { params });
export const fetchProductList = (data: {
  currency?: string;
  group_id?: number;
  limit?: number;
  page?: number;
  in_stock?: number;
  is_sale?: number;
  append_correlation?: number;
}) =>
  httpRequest('POST:/api/products/list', {
    data: {
      ...data,
      ...getStableClientCyyInfo(),
    },
  });

export const fetchProgressiveViewArea = () => httpRequest('GET:/api/lens/view_area');

export const fetchCoatingsShow = () => httpRequest('GET:/api/lens/coatings_show');

// 全量获取可用处方类型列表
export const getPrescriptionTypeAll = () => httpRequest('GET:/api/lens/prescription_type');

export const prescriptionRecognition = (params: {
  prescription_type?: string;
  product_id?: number;
  prescription_img?: string;
}) => httpRequest('GET:/api/lens/prescription_recognition', { params });
// 获取商品列表（客户端）
export const fetchGoodsList = (params: IProductListApiParams) =>
  httpRequest('POST:/api/products/list', { data: { ...params, ...getStableClientCyyInfo() } });

// 支付成功页商品推荐
export const recommendProductsForPaySuccess = (data: {
  limit?: number;
  page?: number;
  append_correlation?: number;
  waterfall_position?: number;
}) =>
  httpRequest('POST:/api/public/recommend_products_for_pay_success', {
    data,
  });

export const fetchSearchResult = (params: {
  search?: string;
  order?: string;
  filter?: any;
  page?: string;
  limit?: string;
  search_type?: number;
}) =>
  // @ts-expect-error - API 端点类型定义不匹配，但接口实际支持 params
  httpRequest('GET:/api/products/search', { params });

// 搜索类型处理接口
export const searchTypeProcessing = (params: { search: string }) =>
  httpRequest('GET:/api/products/search_type_processing', { params });

// 获取商品优惠券提示
// 没有找到满足条件的优惠券数据 就会返回411
export const fetchDetailCouponTips = () =>
  httpRequest('GET:/api/products/get_product_coupon_tips', undefined, {
    success: false,
    fail: false,
  });

export const fetchBffDetailCouponTips = () =>
  httpRequest('GET:/api/bff/get_product_coupon_tips' as any, undefined, {
    success: false,
    fail: false,
  });

// 获取商品评论
export const fetchProductReviews = (params: {
  product_id: string;
  page: string;
  limit: string;
  image?: string;
  appraise_label?: string;
  type?: string;
  sort?: number;
}) => httpRequest('GET:/api/products/reviews', { params });

// 评论点赞
export const postAddHelpful = (data: { review_id: number }) =>
  httpRequest('POST:/api/customer/add_helpful', { data });

// 评论取消点赞
export const postRemoveHelpful = (data: { review_id: number }) =>
  httpRequest('POST:/api/customer/remove_helpful', { data });

export const fetchGoodsDetailRecommend = (params: { shop_sku?: string }) =>
  httpRequest('GET:/api/products/recommend', {
    params: { ...params, ...getStableClientCyyInfo() },
  } as any);

export const fetchRelatedRecommendProducts = (params: { related_recommendation_id?: number }) =>
  httpRequest('GET:/api/products/related_recommendation', { params } as any);

export const postNotifyProduct = (data: { email: string; id: number; notice_type?: string }) =>
  // @ts-ignore - Dynamic URL template not supported by httpRequest type system
  httpRequest(`POST:/api/products/notify/${data.id}`, { data } as any, {
    success: true,
    fail: true,
  });

export const postNotifyPromoListed = (data: { email: string; id: number }) =>
  // @ts-ignore - Dynamic URL template not supported by httpRequest type system
  httpRequest(
    `POST:/api/products/notify/${data.id}` as any,
    { data: { ...data, notice_type: 'promo_listed' } } as any,
    { success: true, fail: false },
  );

export const getCustomerGroupId = (params: { id: number }) =>
  // @ts-ignore - Dynamic URL template not supported by httpRequest type system
  httpRequest(`GET:/api/products/customer_group/${params.id}`, undefined, {
    error: false,
    success: false,
  });

export const postHelpZendesk = (data: {
  email: string;
  phone: string;
  prescription: string;
  images: Array<{ type: string; url: string }>;
}) => httpRequest('POST:/api/customer/prescription/help_zendesk', { data });

export const fetchProductReviewHelpfulNum = (params: { review_ids: string[] }) =>
  httpRequest('GET:/api/products/reviews_helpful_num', { params });
export const fetchPrescriptionFillMethods = () =>
  httpRequest('GET:/api/lens/prescription_filling_method');

// 获取评论图片列表
export const fetchReviewImagesList = (params: {
  page?: number;
  limit: number;
  product_id?: number;
  shop_sku?: string;
}) => httpRequest('GET:/api/products/review_images_list', { params });

export const fetchBffReviewImagesList = (params: {
  limit: number;
  page?: number;
  product_id?: number;
  shop_sku?: string;
}) => httpRequest('GET:/api/bff/review_images_list' as any, { params });

export const fetchBffRelatedRecommendProducts = (
  params: { related_recommendation_id?: number },
  isWeb?: boolean,
) =>
  httpRequest(
    (isWeb
      ? 'GET:/api/bff/related_recommendation_pc'
      : 'GET:/api/bff/related_recommendation_m') as any,
    { params } as any,
  ) as any;

export const fetchBffGoodsDetailRecommend = (params: { shop_sku?: string }, isWeb?: boolean) =>
  httpRequest(
    (isWeb ? 'GET:/api/bff/recommend_pc' : 'GET:/api/bff/recommend_m') as any,
    { params: { ...params, ...getStableClientCyyInfo() } } as any,
  ) as any;

export const fetchBffProductReviews = (params: {
  shop_sku?: string;
  product_id?: string;
  page: string;
  limit: string;
  image?: string;
  appraise_label?: string;
  type?: string;
  sort?: number;
}) => httpRequest('GET:/api/bff/reviews' as any, { params } as any) as any;

export const fetchBffGoodsDetail = (sku: string, isWeb?: boolean, get_promotions?: number) =>
  httpRequest(
    (isWeb ? 'GET:/api/bff/product_detail_pc/{sku}' : 'GET:/api/bff/product_detail_m/{sku}') as any,
    {
      sku,
      params: get_promotions
        ? { get_promotions, ...getStableClientCyyInfo() }
        : { ...getStableClientCyyInfo() },
    } as any,
  ) as any;

export const fetchBffProductDetailGift = (sku: string, ccy?: string) => {
  const stableCurrency = getStableClientCyyInfo() as { cyy?: string; currency?: string };
  const currency = ccy || stableCurrency.cyy || stableCurrency.currency;

  return httpRequest(
    'GET:/api/bff/product_detail_gift/{sku}' as any,
    {
      sku,
      params: currency ? { ccy: currency } : {},
    } as any,
    {
      success: false,
      fail: false,
    },
  ) as Promise<{
    code: number;
    msg: string;
    data: ProductDetailGiftPayload;
  }>;
};
